import React, {useEffect, useState} from 'react';
import { useSearchParams } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import {
    FaExclamationTriangle, FaShieldAlt, FaMoneyBillWave,
    FaCarCrash, FaGlobe, FaCalendarAlt, FaCog, FaGasPump, FaShareAlt, FaDownload
} from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import './styling/FullCheckv2.css';

export default function FullCheckv2() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch(
                process.env.REACT_APP_BASE_URL + "/Vehicle?registration=" + searchParams.get("reg"), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                setNotFound(true);
            } else {
                setData(await response.json());
            }
            setLoading(false);
            setSearchParams(searchParams);
        }
        fetchData().catch(console.error);
    }, []);

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            await navigator.share({ title: `${data?.make} ${data?.model} — Vehicle Check`, url });
        } else {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${parseInt(parts[0])} ${months[parseInt(parts[1]) - 1]} ${parts[2]}`;
        }
        return dateStr;
    };

    if (loading) {
        return (
            <div className='fc-loading'>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className='fc-not-found'>
                <h2>We can't find your car</h2>
                <p>Please email <a href="mailto:info@carinformation.co.uk">info@carinformation.co.uk</a> for an immediate resolution.</p>
            </div>
        );
    }

    return (
        <div className='fc-page'>

            {/* Header */}
            <div className='fc-header'>
                <div className='fc-header-top'>
                    <p className='fc-plate'>{data.vrm}</p>
                    <div className='fc-header-actions'>
                        <button className='fc-share-button' onClick={handleShare}>
                            <FaShareAlt /> {copied ? 'Copied!' : 'Share'}
                        </button>
                        <button className='fc-share-button' onClick={() => window.print()}>
                            <FaDownload /> Save PDF
                        </button>
                    </div>
                </div>
                <h1 className='fc-title'>{data.make} {data.model}</h1>
                <div className='fc-details-bar'>
                    {data.dateFirstRegistered && <span><FaCalendarAlt /> {formatDate(data.dateFirstRegistered)}</span>}
                    {data.fuelType && <span><FaGasPump /> {data.fuelType}</span>}
                    {data.transmissionType && <span><FaCog /> {data.transmissionType}</span>}
                    {data.colour && <span>{data.colour}</span>}
                </div>

                {data.writtenOff && (
                    <div className='fc-alert danger'>
                        <FaExclamationTriangle />
                        This vehicle has been written off{data.writeOffCategory ? ` (Category ${data.writeOffCategory})` : ''}
                        {data.writeOffDate ? ` on ${formatDate(data.writeOffDate)}` : ''}
                    </div>
                )}
                {data.stolen && (
                    <div className='fc-alert danger'>
                        <FaExclamationTriangle />
                        This vehicle has been reported stolen
                    </div>
                )}
                {data.imported && (
                    <div className='fc-alert warning'>
                        <FaExclamationTriangle />
                        This vehicle was imported{data.importedFromOutsideEu ? ' from outside the EU' : ''}
                    </div>
                )}
            </div>

            {/* AI Overview */}
            {data.insight && (
                <div className='fc-section'>
                    <h3 className='fc-section-title'>AI Overview</h3>
                    <p className='fc-insight'>{data.insight}</p>
                </div>
            )}

            {/* Check Summary */}
            <div className='fc-section'>
                <h3 className='fc-section-title'>Check Summary</h3>
                <div className='fc-status-grid'>
                    <div className={`fc-status-item ${data.financeRecordCount === 0 ? 'clear' : 'bad'}`}>
                        <FaMoneyBillWave className='fc-status-icon' />
                        <span className='fc-status-label'>Finance</span>
                        <span className='fc-status-value'>{data.financeRecordCount === 0 ? 'NONE' : `${data.financeRecordCount} RECORD${data.financeRecordCount > 1 ? 'S' : ''}`}</span>
                    </div>
                    <div className={`fc-status-item ${!data.writtenOff ? 'clear' : 'bad'}`}>
                        <FaCarCrash className='fc-status-icon' />
                        <span className='fc-status-label'>Write-Off</span>
                        <span className='fc-status-value'>{data.writtenOff ? (data.writeOffCategory || 'YES') : 'NONE'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.stolen ? 'clear' : 'bad'}`}>
                        <FaShieldAlt className='fc-status-icon' />
                        <span className='fc-status-label'>Stolen</span>
                        <span className='fc-status-value'>{data.stolen ? 'YES' : 'NONE'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.scrapped ? 'clear' : 'bad'}`}>
                        <FaCarCrash className='fc-status-icon' />
                        <span className='fc-status-label'>Scrapped</span>
                        <span className='fc-status-value'>{data.scrapped ? 'YES' : 'NO'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.exported ? 'clear' : 'warn'}`}>
                        <FaGlobe className='fc-status-icon' />
                        <span className='fc-status-label'>Exported</span>
                        <span className='fc-status-value'>{data.exported ? 'YES' : 'NO'}</span>
                    </div>
                    <div className={`fc-status-item ${!data.imported ? 'clear' : 'warn'}`}>
                        <FaGlobe className='fc-status-icon' />
                        <span className='fc-status-label'>Imported</span>
                        <span className='fc-status-value'>{data.imported ? 'YES' : 'NO'}</span>
                    </div>
                </div>
            </div>

            {/* Key Details */}
            <div className='fc-section'>
                <h3 className='fc-section-title'>Key Details</h3>
                <div className='fc-details-grid'>
                    {data.dateFirstRegistered && <div className='fc-detail-item'><span className='fc-detail-label'>First Registered</span><span className='fc-detail-value'>{formatDate(data.dateFirstRegistered)}</span></div>}
                    {data.latestV5cIssuedDate && <div className='fc-detail-item'><span className='fc-detail-label'>Last V5c Issued</span><span className='fc-detail-value'>{formatDate(data.latestV5cIssuedDate)}</span></div>}
                    {data.latestKeeperChangeDate && <div className='fc-detail-item'><span className='fc-detail-label'>Last Keeper Change</span><span className='fc-detail-value'>{formatDate(data.latestKeeperChangeDate)}</span></div>}
                    <div className='fc-detail-item'><span className='fc-detail-label'>Previous Keepers</span><span className='fc-detail-value'>{data.previousKeeperCount}</span></div>
                    {data.vinLast5 && <div className='fc-detail-item'><span className='fc-detail-label'>VIN Last 5</span><span className='fc-detail-value'>{data.vinLast5}</span></div>}
                    {data.yearOfManufacture && <div className='fc-detail-item'><span className='fc-detail-label'>Year of Manufacture</span><span className='fc-detail-value'>{data.yearOfManufacture}</span></div>}
                    {data.engineCapacity > 0 && <div className='fc-detail-item'><span className='fc-detail-label'>Engine</span><span className='fc-detail-value'>{data.engineCapacity}cc</span></div>}
                    {data.transmissionType && <div className='fc-detail-item'><span className='fc-detail-label'>Transmission</span><span className='fc-detail-value'>{data.transmissionType}</span></div>}
                    {data.colour && <div className='fc-detail-item'><span className='fc-detail-label'>Colour</span><span className='fc-detail-value'>{data.colour}</span></div>}
                    {data.fuelType && <div className='fc-detail-item'><span className='fc-detail-label'>Fuel Type</span><span className='fc-detail-value'>{data.fuelType}</span></div>}
                    {data.plateChangeCount > 0 && <div className='fc-detail-item'><span className='fc-detail-label'>Plate Changes</span><span className='fc-detail-value'>{data.plateChangeCount}</span></div>}
                </div>
            </div>

            {/* Mileage History */}
            {data.mileageRecordList && data.mileageRecordList.length > 0 && (() => {
                const chartData = [...data.mileageRecordList]
                    .sort((a, b) => {
                        const [ad, am, ay] = a.dateOfInformation.split('/');
                        const [bd, bm, by] = b.dateOfInformation.split('/');
                        return new Date(`${ay}-${am}-${ad}`) - new Date(`${by}-${bm}-${bd}`);
                    })
                    .filter((r, i, arr) => i === 0 || r.dateOfInformation !== arr[i - 1].dateOfInformation)
                    .map(r => {
                        const [d, m, y] = r.dateOfInformation.split('/');
                        return { date: `${m}/${y.slice(2)}`, mileage: r.mileage };
                    });
                return (
                    <div className='fc-section'>
                        <h3 className='fc-section-title'>Mileage History</h3>
                        {data.mileageAnomalyDetected && (
                            <div className='fc-alert warning' style={{ marginBottom: 10 }}>
                                <FaExclamationTriangle /> Mileage anomaly detected — readings may be inconsistent
                            </div>
                        )}
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#6b7280' }} width={36} />
                                <Tooltip formatter={v => v.toLocaleString() + ' mi'} labelStyle={{ color: '#111827', fontWeight: 600 }} />
                                <Line type="monotone" dataKey="mileage" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: '#16a34a' }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                );
            })()}

            {/* Finance Records */}
            {data.financeRecordList && data.financeRecordList.length > 0 && (
                <div className='fc-section'>
                    <h3 className='fc-section-title'>Finance Records</h3>
                    {data.financeRecordList.map((record, i) => record && (
                        <div key={i} className='fc-finance-record'>
                            {record.financeCompany && <div className='fc-detail-item'><span className='fc-detail-label'>Finance Company</span><span className='fc-detail-value'>{record.financeCompany}</span></div>}
                            {record.agreementType && <div className='fc-detail-item'><span className='fc-detail-label'>Agreement Type</span><span className='fc-detail-value'>{record.agreementType}</span></div>}
                            {record.agreementDate && <div className='fc-detail-item'><span className='fc-detail-label'>Agreement Date</span><span className='fc-detail-value'>{record.agreementDate.split("T")[0]}</span></div>}
                            {record.agreementTerm && <div className='fc-detail-item'><span className='fc-detail-label'>Term</span><span className='fc-detail-value'>{record.agreementTerm} months</span></div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Plate Changes */}
            {data.plateChangeList && data.plateChangeList.length > 0 && (
                <div className='fc-section'>
                    <h3 className='fc-section-title'>Plate Changes</h3>
                    <div className='fc-details-grid'>
                        {data.plateChangeList.map((change, i) => change && (
                            <div key={i} className='fc-detail-item'>
                                <span className='fc-detail-label'>Previous Plate</span>
                                <span className='fc-detail-value'>{change.previousVrm}{change.dateChanged ? ` — ${formatDate(change.dateChanged)}` : ''}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
}
